import React, { useState, useRef, useEffect } from "react";
import Slider from "react-slick";
import { StoryblokAsset } from "../../../interfaces/Storyblok/StoryblokAsset.interface";
import { StoryblokRichtext } from "storyblok-rich-text-react-renderer";
import Blok from "../../../interfaces/Storyblok/Blok.interface";
import Image from "../../atoms/Image/Image.atom";
import RichText from "../../atoms/RichText/RichText.atom";
import Button from "../../atoms/Button/Button.component";
import { ButtonSizeTypes } from "../../../interfaces/enums/ButtonSizeTypes.enum";
import { ButtonTypes } from "../../../interfaces/enums/ButtonTypes.enum";
import isEmptyString from "../../../utils/isEmptyString/isEmptyString.util";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../../../styles/carousel.css";

interface CarouselCard {
  background_image: StoryblokAsset;
  mobile_background: StoryblokAsset;
  heading: string;
  body: StoryblokRichtext;
  primary_cta: Blok[];
  secondary_cta?: Blok[];
}

interface ImageCarouselHeroProps {
  cards: CarouselCard[];
  slide_duration?: number; // 3-10 seconds
  enable_gradient?: boolean;
  text_alignment?: "left" | "center" | "right";
  auto_rotate?: boolean;
  height?: string; // sm, md, lg
}

const ImageCarouselHero: React.FC<ImageCarouselHeroProps> = ({
  cards = [],
  slide_duration = 5,
  enable_gradient = true,
  text_alignment = "left",
  auto_rotate = true,
  height,
}) => {
  const [isPlaying, setIsPlaying] = useState(auto_rotate);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showContent, setShowContent] = useState(false);
  const sliderRef = useRef<Slider>(null);

  // Respect user's motion preferences
  const prefersReducedMotion =
    typeof window !== "undefined" ? window.matchMedia("(prefers-reduced-motion: reduce)").matches : false;

  const shouldAutoPlay = auto_rotate && !prefersReducedMotion && cards.length > 1;

  // Handle content fade-in timing
  useEffect(() => {
    // Hide content immediately when slide changes
    setShowContent(false);

    // Show content after 500ms
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [currentSlide]);

  // Show content immediately on first load
  useEffect(() => {
    const initialTimer = setTimeout(() => {
      setShowContent(true);
    }, 500);

    return () => clearTimeout(initialTimer);
  }, []);
  const settings = {
    dots: false, // We'll create custom indicators
    infinite: cards.length > 1,
    speed: 1000, // Slower transition for fade effect
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false, // We'll create custom arrows
    autoplay: shouldAutoPlay && isPlaying,
    autoplaySpeed: slide_duration * 1000,
    pauseOnHover: true,
    beforeChange: (current: number, next: number) => setCurrentSlide(next),
    accessibility: true,
    swipe: true, // Enable swipe on mobile only (controlled by responsive settings)
    swipeToSlide: true, // Allow swiping to any slide on mobile
    touchMove: true, // Enable touch movement on mobile
    touchThreshold: 10, // Sensitivity for touch
    fade: true, // Enable fade transition between slides
    cssEase: "ease-in-out", // Smooth easing
    responsive: [
      {
        breakpoint: 768, // Below tablet size (mobile)
        settings: {
          swipe: true,
          swipeToSlide: true,
          touchMove: true,
        },
      },
      {
        breakpoint: 9999, // Desktop and above
        settings: {
          swipe: false,
          swipeToSlide: false,
          touchMove: false,
        },
      },
    ],
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
    if (sliderRef.current) {
      if (isPlaying) {
        sliderRef.current.slickPause();
      } else {
        sliderRef.current.slickPlay();
      }
    }
  };

  const goToSlide = (slideIndex: number) => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(slideIndex);
    }
  };

  const goToNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const goToPrevious = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        if (sliderRef.current) {
          sliderRef.current.slickPrev();
        }
        break;
      case "ArrowRight":
        event.preventDefault();
        if (sliderRef.current) {
          sliderRef.current.slickNext();
        }
        break;
      case " ": // Space bar
        event.preventDefault();
        togglePlayPause();
        break;
    }
  };

  const getTextAlignmentClass = () => {
    switch (text_alignment) {
      case "center":
        return "text-center items-center";
      case "right":
        return "text-right items-end";
      default:
        return "text-left items-start";
    }
  };

  const getHeightClass = () => {
    switch (height) {
      case "sm":
        return "h-[55vh] md:h-[60vh] lg:h-[65vh]";
      case "md":
        return "h-[60vh] md:h-[65vh] lg:h-[70vh]";
      case "lg":
      default:
        return "h-[65vh] md:h-[70vh] lg:h-[75vh]";
    }
  };

  const isMobileBackgroundAvailable = (card: CarouselCard): boolean => {
    return card.mobile_background && !isEmptyString(card.mobile_background.filename);
  };

  if (!cards || cards.length === 0) {
    return null;
  }

  return (
    <div
      className={`image-carousel-hero relative w-full ${getHeightClass()} overflow-hidden`}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="region"
      aria-label="Image carousel"
    >
      <Slider {...settings} ref={sliderRef} className="h-full">
        {cards.map((card, index) => (
          <div key={index} className={`relative ${getHeightClass()}`}>
            {/* Mobile Background Image - Shows only on small screens when available */}
            {isMobileBackgroundAvailable(card) && (
              <div className="absolute inset-0 w-full h-full block sm:hidden">
                <Image image={card.mobile_background} objectFit="cover" className="w-full h-full" fluidMaxWidth={768} />
              </div>
            )}

            {/* Desktop/Tablet Background Image - Hidden on small screens when mobile background is available */}
            <div className={`absolute inset-0 w-full h-full ${isMobileBackgroundAvailable(card) ? "hidden sm:block" : ""}`}>
              <Image image={card.background_image} objectFit="cover" className="w-full h-full" fluidMaxWidth={2560} fluidSizes="(max-width: 768px) 200vw, 100vw" />
            </div>

            {/* Gradient Overlay - Only show if there's content and gradient is enabled */}
            {enable_gradient &&
              (card.heading ||
                card.body ||
                (card.primary_cta && card.primary_cta.length > 0) ||
                (card.secondary_cta && card.secondary_cta.length > 0)) && (
                <div
                  className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent pointer-events-none"
                  style={{
                    background: "linear-gradient(to right, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.4), transparent)",
                  }}
                />
              )}

            {/* Card Content - Only show if there's content */}
            {(card.heading ||
              card.body ||
              (card.primary_cta && card.primary_cta.length > 0) ||
              (card.secondary_cta && card.secondary_cta.length > 0)) && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="container mx-auto h-full py-12 md:py-16 xl:pb-16 xl:pt-24 relative z-10">
                  <div
                    className={`max-w-4xl ${getTextAlignmentClass()} transition-all duration-1000 ease-in-out ${
                      showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                    }`}
                  >
                    {/* Heading */}
                    {card.heading && (
                      <h1 className="text-2xl md:text-3xl xl:text-4xl 3xl:text-5xl font-extrabold text-White pb-6 2xl:pb-3">
                        {card.heading}
                      </h1>
                    )}

                    {/* Body Text */}
                    {card.body && (
                      <div className="text-base lg:text-lg font-semibold prose-p:text-White pb-10">
                        <RichText content={card.body} />
                      </div>
                    )}

                    {/* Buttons */}
                    <div className="md:flex space-x-0 md:space-x-4 space-y-4 md:space-y-0 pointer-events-auto">
                      {card.primary_cta && card.primary_cta.length > 0 && (
                        <Button
                          link={(card.primary_cta[0] as any).link}
                          label={(card.primary_cta[0] as any).label}
                          type={ButtonTypes.PRIMARY}
                          size={ButtonSizeTypes.XL}
                          className="!block lg:!inline-block"
                        />
                      )}
                      {card.secondary_cta && card.secondary_cta.length > 0 && (
                        <Button
                          link={(card.secondary_cta[0] as any).link}
                          label={(card.secondary_cta[0] as any).label}
                          type={ButtonTypes.SECONDARY}
                          size={ButtonSizeTypes.XL}
                          className="!block lg:!inline-block"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </Slider>

      {/* Controls - Only show if more than 1 card */}
      {cards.length > 1 && (
        <>
          {/* Navigation Arrows - Desktop Only */}
          <button
            onClick={goToPrevious}
            className="hidden md:flex absolute left-6 top-1/2 transform -translate-y-1/2 w-12 h-12 backdrop-blur-sm rounded-full items-center justify-center transition-all duration-200 z-10"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.2)", color: "rgb(255, 255, 255)" }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.3)")}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.2)")}
            aria-label="Previous slide"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </button>

          <button
            onClick={goToNext}
            className="hidden md:flex absolute right-6 top-1/2 transform -translate-y-1/2 w-12 h-12 backdrop-blur-sm rounded-full items-center justify-center transition-all duration-200 z-10"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.2)", color: "rgb(255, 255, 255)" }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.3)")}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.2)")}
            aria-label="Next slide"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
            </svg>
          </button>

          {/* Bottom Controls */}
          <div className="absolute bottom-6 left-6 flex items-center gap-4 z-10">
            {/* Play/Pause Button */}
            {shouldAutoPlay && (
              <button
                onClick={togglePlayPause}
                className="w-10 h-10 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-200"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.2)", color: "rgb(255, 255, 255)" }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.3)")}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.2)")}
                aria-label={isPlaying ? "Pause carousel" : "Play carousel"}
              >
                {isPlaying ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>
            )}

            {/* Dot Indicators - Clickable on desktop, visual only on mobile */}
            <div className="flex gap-3" role="tablist" aria-label="Carousel slides">
              {cards.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className="w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-200 md:hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
                  style={{
                    backgroundColor: index === currentSlide ? "rgb(255, 255, 255)" : "rgba(255, 255, 255, 0.3)",
                    cursor: typeof window !== "undefined" && window.innerWidth >= 768 ? "pointer" : "default",
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                  role="tab"
                  aria-selected={index === currentSlide}
                  tabIndex={typeof window !== "undefined" && window.innerWidth >= 768 ? 0 : -1}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Screen Reader Only Current Slide Info */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        Slide {currentSlide + 1} of {cards.length}
        {cards[currentSlide]?.heading && `: ${cards[currentSlide].heading}`}
      </div>
    </div>
  );
};

export default ImageCarouselHero;
